"use strict";

/** @format */
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ModalUtils = void 0;
/**
 * Utility functions for positioning popovers, dropdowns, and modals
 */
exports.ModalUtils = {
  /**
   * Sets the positioning style for a modal element based on its type and anchor element
   */
  setPositioningStyle: options => {
    const {
      anchorElement,
      modalElement,
      backgroundElement,
      direction = 'bottom',
      type = 'modal',
      popoverCaret = false,
      popoverAlignment = 'center',
      attachedBackground = false
    } = options;
    if (!modalElement || !anchorElement) {
      console.warn('PopoverUtils: Missing required elements for positioning.');
      return;
    }
    const anchorRect = anchorElement.getBoundingClientRect();
    // Helper function to calculate horizontal position for popover
    const calculatePopoverHorizontalPosition = (anchorRect, elementRect) => {
      const horizontalPositions = {
        left: anchorRect.left,
        right: anchorRect.right - elementRect.width,
        center: anchorRect.left + anchorRect.width / 2 - elementRect.width / 2
      };
      // Return the position for the current alignment or default to center
      return horizontalPositions[popoverAlignment] || horizontalPositions.center;
    };
    // Helper function to calculate vertical position for popover
    const calculatePopoverVerticalPosition = (anchorRect, elementRect) => {
      const verticalPositions = {
        left: anchorRect.top,
        right: anchorRect.bottom - elementRect.height,
        center: anchorRect.top + anchorRect.height / 2 - elementRect.height / 2
      };
      // Return the position for the current alignment or default to center
      return verticalPositions[popoverAlignment] || verticalPositions.center;
    };
    // Helper function to position popover based on direction
    const positionPopover = () => {
      const elementRect = modalElement.getBoundingClientRect();
      const caretHeight = popoverCaret ? 12 : 0; // 12px is the default size for --hmf-sm
      // Reset all positions first
      modalElement.style.top = '';
      modalElement.style.bottom = '';
      modalElement.style.left = '';
      modalElement.style.right = '';
      const popoverDirection = direction || 'bottom';
      const horizontalPosition = calculatePopoverHorizontalPosition(anchorRect, elementRect);
      const verticalPosition = calculatePopoverVerticalPosition(anchorRect, elementRect);
      // Define positioning for each direction
      const popoverPositions = {
        top: {
          top: `${anchorRect.top - elementRect.height - caretHeight}px`,
          left: `${horizontalPosition}px`
        },
        right: {
          top: `${verticalPosition}px`,
          left: `${anchorRect.right + caretHeight}px`
        },
        left: {
          top: `${verticalPosition}px`,
          left: `${anchorRect.left - elementRect.width - caretHeight}px`
        },
        bottom: {
          top: `${anchorRect.bottom + caretHeight}px`,
          left: `${horizontalPosition}px`
        }
      };
      // Get the position values for the current direction (fallback to bottom if not found)
      const position = popoverPositions[popoverDirection] || popoverPositions.bottom;
      // Apply the position values
      modalElement.style.top = position.top;
      modalElement.style.left = position.left;
    };
    // Helper function to set drawer height
    const setDrawerHeight = () => {
      if (direction === 'left' || direction === 'right') {
        modalElement.style.maxHeight = `calc(100% - ${anchorRect.bottom}px)`;
      } else {
        modalElement.style.maxHeight = `100%`;
      }
    };
    // Helper function to position background for drawer
    const positionDrawerBackground = () => {
      if (!attachedBackground || !backgroundElement) return;
      if (direction !== 'bottom') {
        backgroundElement.style.top = anchorRect.bottom < 0 ? '0px' : `${anchorRect.bottom}px`;
      } else {
        backgroundElement.style.bottom = anchorRect.top > window.innerHeight ? '0' : `${window.innerHeight - anchorRect.top}px`;
      }
    };
    // Calculate position values for different scenarios
    const calculatePosition = {
      // Calculate maximum height for modal
      maxHeight: element => {
        if (anchorRect.bottom + element.clientHeight > window.innerHeight) {
          return `${window.innerHeight - anchorRect.bottom - 20}px`;
        }
        return 'none';
      },
      // Calculate left position for modal
      left: element => {
        // Check if modal would extend beyond right edge of viewport
        if (anchorRect.left + element.clientWidth > window.innerWidth) {
          // Adjust left position to keep modal within viewport
          return `${anchorRect.left - (element.clientWidth - (window.innerWidth + window.scrollX - anchorRect.left)) - 20}px`;
        }
        // Default left position aligned with anchor
        return `${anchorRect.left + window.scrollX}px`;
      },
      // Calculate top position for modal
      top: element => {
        // Special case for drawer type
        if (type === 'drawer') {
          return `${anchorRect.bottom}px`;
        }
        // Check if modal would extend beyond bottom of viewport
        if (anchorRect.bottom + element.clientHeight > window.innerHeight) {
          if (element.clientHeight > window.innerHeight) {
            // Modal is larger than viewport height, add scrolling
            element.style.overflowY = 'scroll';
            element.style.maxHeight = calculatePosition.maxHeight(element);
            return `${anchorRect.bottom + window.scrollY}px`;
          } else {
            // Adjust position to keep modal within viewport
            return `${anchorRect.bottom - (element.scrollHeight - (window.innerHeight + window.scrollY - anchorRect.bottom)) - 20}px`;
          }
        }
        // Default top position below anchor
        return `${anchorRect.bottom + window.scrollY}px`;
      },
      // Calculate bottom position for modal
      bottom: element => {
        if (anchorRect.top - element.scrollHeight < 0) {
          // Adjust if modal would extend above viewport
          return `${window.innerHeight - element.scrollHeight}px`;
        }
        // Default bottom position
        return `${window.innerHeight - (anchorRect.bottom - anchorElement.scrollHeight)}px`;
      }
    };
    // Helper function to set dropdown styles
    const setDropdownStyles = () => {
      if (!anchorElement.offsetWidth) return;
      modalElement.style.width = 'auto';
      modalElement.style.minWidth = `${anchorElement.offsetWidth}px`;
      modalElement.style.left = calculatePosition.left(modalElement);
    };
    // Handle positioning based on direction for non-popover types
    const handleNonPopoverPositioning = () => {
      if (direction !== 'bottom') {
        modalElement.style.top = calculatePosition.top(modalElement);
      } else {
        modalElement.style.bottom = calculatePosition.bottom(modalElement);
      }
    };
    // Define handlers for each type
    const typeHandlers = {
      popover: positionPopover,
      drawer: () => {
        handleNonPopoverPositioning();
        setDrawerHeight();
        positionDrawerBackground();
      },
      dropdown: () => {
        handleNonPopoverPositioning();
        setDropdownStyles();
      },
      // Default handler for modal and dialog types
      default: handleNonPopoverPositioning
    };
    // Execute the appropriate handler or fall back to default
    const handler = typeHandlers[type] || typeHandlers['default'];
    handler();
  }
};
